/**
 * @file CarouselNavigation.tsx
 * @description Navigation arrows for carousel components
 *
 * Responsive navigation:
 * - Desktop: Side arrows positioned at carousel middle
 * - Mobile: Bottom arrows centered below carousel
 */

import type { ReactElement } from "react";
import { Button } from "@lumi/ui-headless";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselNavigationProps {
  readonly onPrevious: () => void;
  readonly onNext: () => void;
}

/**
 * Navigation arrows for carousel
 * Desktop: Positioned on sides at middle of carousel
 * Mobile: Positioned below carousel in a row
 */
export function CarouselNavigation({ onPrevious, onNext }: CarouselNavigationProps): ReactElement {
  return (
    <>
      {/* Desktop: Side arrows - positioned absolutely relative to parent section */}
      {/* Hidden on mobile & tablet portrait to avoid swipe gesture conflicts */}
      <div className="hidden lg:block absolute left-4 xl:left-8 top-1/2 -translate-y-1/2 z-20">
        <Button onClick={onPrevious} variant="ghost-sm" aria-label="Previous">
          <ChevronLeft size={36} />
        </Button>
      </div>
      <div className="hidden lg:block absolute right-4 xl:right-8 top-1/2 -translate-y-1/2 z-20">
        <Button onClick={onNext} variant="ghost-sm" aria-label="Next">
          <ChevronRight size={36} />
        </Button>
      </div>

      {/* Mobile + Tablet Portrait: Bottom arrows for better touch access */}
      <div className="lg:hidden absolute bottom-2 left-0 right-0 flex justify-center gap-4 z-20">
        <Button onClick={onPrevious} variant="ghost-sm" aria-label="Previous">
          <ChevronLeft size={28} />
        </Button>
        <Button onClick={onNext} variant="ghost-sm" aria-label="Next">
          <ChevronRight size={28} />
        </Button>
      </div>
    </>
  );
}
